import {
  AloftArea,
  AloftPrice,
  SiteRating,
  orUndefined,
  schAlertType,
  schAloftArea,
  schAloftPrice,
  schPairedCostRouteItem,
  schSiteRating,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';

export const inconsistentCompSiteAdjustment: AlertDefinition = ({
  compAdjustments,
  compSequence,
  propertyDataEntities,
}) => {
  const subjectSite = (propertyDataEntities.subject as SubjectPropertyDataState | undefined)
    ?.subjectData.editableData.site;

  const subjectSiteArea = orUndefined(schAloftArea.parse)(subjectSite?.area);
  const subjectInfluenceRating = orUndefined(schSiteRating.parse)(subjectSite?.influenceRating);
  const subjectViewRating = orUndefined(schSiteRating.parse)(subjectSite?.viewRating);

  if (!subjectSiteArea || !subjectInfluenceRating || !subjectViewRating) {
    return undefined;
  }

  const faultyProperty = compSequence
    .map((id, index) => {
      const { area, influenceRating, viewRating } =
        (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData.editableData
          .site ?? {};

      const siteSizeAdjustment = compAdjustments[id]?.site.size.adjustment;

      return {
        area: orUndefined(schAloftArea.parse)(area),
        id,
        index,
        influenceRating: orUndefined(schSiteRating.parse)(influenceRating),
        siteSizeAdjustment: orUndefined(schAloftPrice.parse)(siteSizeAdjustment),
        viewRating: orUndefined(schSiteRating.parse)(viewRating),
      };
    })
    .filter(
      (
        c,
      ): c is {
        area: AloftArea;
        id: string;
        index: number;
        influenceRating: SiteRating;
        siteSizeAdjustment: AloftPrice;
        viewRating: SiteRating;
      } => !!c.area && !!c.siteSizeAdjustment && !!c.influenceRating && !!c.viewRating,
    )
    .find(({ area, influenceRating, siteSizeAdjustment, viewRating }) => {
      if (influenceRating !== subjectInfluenceRating) return false;
      if (viewRating !== subjectViewRating) return false;

      if (siteSizeAdjustment.gt(AloftPrice.zero) && area.le(subjectSiteArea)) return true;
      if (siteSizeAdjustment.lt(AloftPrice.zero) && area.ge(subjectSiteArea)) return true;

      return false;
    });

  if (!faultyProperty) return undefined;

  const { id: propertyID, area, siteSizeAdjustment } = faultyProperty;
  const propertyName =
    propertyID === 'subject' ? 'Subject' : `Comparable ${faultyProperty.index + 1}`;

  let siteRelationship = 'an equal';
  if (area.gt(subjectSiteArea)) {
    siteRelationship = 'a larger';
  } else if (area.lt(subjectSiteArea)) {
    siteRelationship = 'a smaller';
  }

  return {
    type: schAlertType.enum.inconsistentCompSiteAdjustment,
    status: 'active',
    message: `${propertyName} is adjusted ${siteSizeAdjustment.gt(AloftPrice.zero) ? 'up' : 'down'}, despite having ${siteRelationship} Lot Size than the Subject (${propertyName}: ${area.toString()} | Subject: ${subjectSiteArea.toString()}).`,
    propertyID,
    fixingInfo: {
      tips: 'Tips: The site adjustment appears to be going in the wrong direction. The Subject and Comparable have the same view and site influences, but the adjustment does not align with the difference in Site Size.',
      pairedCostRouteItem: schPairedCostRouteItem.enum.analysis,
    },
  };
};
